import { FileOutlined, FolderOutlined, LeftOutlined } from '@ant-design/icons';
import { Input, Space, Table } from 'antd';
import { Document, Investment, queries } from 'procura-shared';
import React, { useEffect, useState } from 'react';
import { useCollectionData, useDocumentData } from 'react-firebase-hooks/firestore';
import { useTranslation } from 'react-i18next';
import calls from '../api/calls';
import { DetailsAddButton, Editable, UploadButton } from './Components';

type InvestmentDocumentsProps = {
  investment: Investment;
  loading?: boolean;
  adminMode?: boolean;
};

export const InvestmentDocuments = ({ investment, loading = false, adminMode = false }: InvestmentDocumentsProps) => {
  const { t } = useTranslation();
  const [currentPath, setCurrentPath] = useState<string>('root');
  const [documents, setDocuments] = useState<Document[]>([]);

  const dbPath = (path) => path.replace(/\//g, '|');
  const currentPathString = (): string => currentPath.replace('root', investment?.name ?? '').replace('/', ' / ');

  const [fbFolders] = useCollectionData<Object>(queries.investmentDocumentsQuery(investment?.id), { idField: 'id' });
  const [fbFiles] = useDocumentData<Object>(queries.investmentFilesQuery(investment?.id, dbPath(currentPath)));

  useEffect(() => {
    let folders: Document[] = [];
    (fbFolders ?? []).forEach(function (doc) {
      if (doc['id'].includes(dbPath(currentPath)) && doc['id'].indexOf('|') >= 1) {
        let newString = doc['id'].replace(dbPath(currentPath), '');
        newString = newString.replace('|', '');
        if (newString.indexOf('|') >= 1) {
          newString = newString.slice(0, newString.indexOf('|'));
        }
        if (newString.length >= 1) {
          if (folders.findIndex((f) => f.name === newString) === -1) {
            folders.push({ type: 'folder', name: newString });
          }
        }
      }
    });
    let files: Document[] = Object.keys(fbFiles ?? {}).map((f) => ({ type: 'file', name: f, url: fbFiles[f] }));
    setDocuments([...folders, ...files]);
  }, [fbFolders, fbFiles, currentPath]);

  const folderAddHandler = (value: string) => {
    setDocuments([...documents, { type: 'folder', name: value }]);
  };

  const folderClickHandler = (e, folder: string) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentPath(`${currentPath}/${folder}`);
  };

  const fileDeleteHandler = (documentName: string, documentUrl: string) => {
    calls.investments.deleteDocument({
      investmentId: investment.id,
      documentPath: dbPath(currentPath),
      documentName,
      documentUrl,
    });
  };

  const backClickHandler = () => {
    if (currentPath !== 'root') {
      setCurrentPath(currentPath.slice(0, currentPath.lastIndexOf('/')));
    }
  };

  const renderTableFolderItem = (name: string) => (
    <Space>
      <FolderOutlined onClick={(e) => folderClickHandler(e, name)} />
      <Editable value={name} editMode={false} onClick={(e) => folderClickHandler(e, name)} />
    </Space>
  );

  const renderTableFileItem = (name: string, url: string) => (
    <Space>
      <FileOutlined />
      <Editable
        editMode={adminMode ? 'delete' : false}
        value={name}
        onDelete={() => fileDeleteHandler(name, url)}
        render={
          <a href={url} target="_blank" rel="noopener noreferrer">
            {name}
          </a>
        }
      />
    </Space>
  );

  const renderTableItem = (_text, item, _index) =>
    item.type === 'folder' ? renderTableFolderItem(item.name) : renderTableFileItem(item.name, item.url);

  const renderTableTitle = () => (
    <Input
      type="text"
      prefix={<LeftOutlined onClick={() => backClickHandler()} />}
      suffix={
        adminMode && (
          <Space>
            <DetailsAddButton label={t('addFolder')} placeholder={t('folderName')} onConfirm={folderAddHandler} />
            <UploadButton currentPath={currentPath} investmentId={investment?.id} />
          </Space>
        )
      }
      onClick={() => backClickHandler()}
      readOnly={true}
      placeholder={currentPathString()}
    />
  );

  const tableColumns = [
    {
      title: renderTableTitle,
      dataIndex: 'name',
      key: 'name',
      render: renderTableItem,
    },
  ];

  return (
    <Table
      loading={loading}
      size="small"
      bordered
      dataSource={documents.map((d) => ({ ...d, key: d.name }))}
      columns={tableColumns}
      pagination={false}
      scroll={{ y: 300 }}
    />
  );
};
